import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getOrCreateReferralCode, getReferralStats } from '../../services/referralService';

const APP_BASE_URL = 'https://loopnesta4315.builtwithrocket.new';

export default function CreatorReferralsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [referralCode, setReferralCode] = useState(null);
  const [stats, setStats] = useState({ totalReferrals: 0, totalCreditsEarned: 0, referrals: [] });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const referralLink = referralCode ? `${APP_BASE_URL}/home-landing-screen?ref=${referralCode}` : '';

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [codeResult, statsResult] = await Promise.all([
        getOrCreateReferralCode(user?.id),
        getReferralStats(user?.id),
      ]);
      if (codeResult?.error) setError(codeResult?.error);
      else setReferralCode(codeResult?.code);
      setStats(statsResult);
    } catch (err) {
      setError(err?.message || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/home-landing-screen');
      return;
    }
    loadData();
  }, [isAuthenticated, loadData, navigate]);

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard?.writeText(referralLink)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyCode = () => {
    if (!referralCode) return;
    navigator.clipboard?.writeText(referralCode)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors"
          >
            <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
              <Icon name="Users" size={16} color="white" />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Referral Program</h1>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Invite creators, earn credits</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col gap-6">
        {/* Error */}
        {error && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
            {error}
          </div>
        )}

        {/* Hero Banner */}
        <div className="rounded-2xl p-6 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))', border: '1px solid rgba(99,102,241,0.3)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
            <Icon name="Gift" size={28} color="white" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Invite & Earn Credits</h2>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-body)' }}>
            Share your referral link. When a new creator signs up, you both get free compute credits.
          </p>
        </div>

        {/* Reward Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.15)' }}>
              <Icon name="Zap" size={22} color="#6366F1" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#6366F1', fontFamily: 'var(--font-data)' }}>+100</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Credits for You</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Per successful referral</p>
            </div>
          </div>
          <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.15)' }}>
              <Icon name="UserPlus" size={22} color="#10B981" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#10B981', fontFamily: 'var(--font-data)' }}>+50</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Credits for New User</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Bonus on signup</p>
            </div>
          </div>
        </div>

        {/* Referral Link Section */}
        <div className="rounded-2xl p-5 flex flex-col gap-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Your Referral Link</h3>

          {loading ? (
            <div className="h-12 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
          ) : (
            <>
              {/* Code badge */}
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                  <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-sm font-mono font-bold tracking-widest" style={{ color: 'var(--color-foreground)' }}>
                    {referralCode || '—'}
                  </span>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="px-4 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                  style={{ background: 'rgba(99,102,241,0.15)', color: '#6366F1', border: '1px solid rgba(99,102,241,0.3)' }}
                >
                  Copy Code
                </button>
              </div>

              {/* Full link */}
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                <Icon name="Link" size={14} color="var(--color-muted-foreground)" />
                <span className="flex-1 text-xs truncate" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  {referralLink || 'Generating link...'}
                </span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                  style={{ background: copied ? '#10B981' : '#6366F1', color: 'white' }}
                >
                  <Icon name={copied ? 'Check' : 'Copy'} size={12} color="white" />
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl p-5 text-center" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            {loading ? (
              <div className="h-8 w-16 mx-auto rounded animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <p className="text-3xl font-bold" style={{ color: '#6366F1', fontFamily: 'var(--font-data)' }}>{stats?.totalReferrals}</p>
            )}
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Total Referrals</p>
          </div>
          <div className="rounded-2xl p-5 text-center" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            {loading ? (
              <div className="h-8 w-16 mx-auto rounded animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <p className="text-3xl font-bold" style={{ color: '#10B981', fontFamily: 'var(--font-data)' }}>{stats?.totalCreditsEarned}</p>
            )}
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Credits Earned</p>
          </div>
        </div>

        {/* Referral History */}
        {stats?.referrals?.length > 0 && (
          <div className="rounded-2xl p-5 flex flex-col gap-3" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Referral History</h3>
            <div className="flex flex-col gap-2">
              {stats?.referrals?.map((r, idx) => (
                <div key={r?.id} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.2)' }}>
                      <Icon name="User" size={13} color="#6366F1" />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Creator #{idx + 1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold" style={{ color: '#10B981' }}>+100 credits</span>
                    <span className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      {new Date(r.created_at)?.toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="rounded-2xl p-5 flex flex-col gap-3" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>How It Works</h3>
          {[
            { step: '1', icon: 'Share2', text: 'Share your unique referral link with other creators' },
            { step: '2', icon: 'UserPlus', text: 'They sign up using your link and get +50 bonus credits' },
            { step: '3', icon: 'Zap', text: 'You automatically receive +100 compute credits' },
          ]?.map((item) => (
            <div key={item?.step} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
                <Icon name={item?.icon} size={14} color="#6366F1" />
              </div>
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-body)' }}>{item?.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
